"use client";

import { c as _c } from "react/compiler-runtime";
import { useDesktopAnimations } from "@/lib/breakpoints";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { simplifyPoints } from "./brokerage.data";
import GradientButton from "@/components/ui/GradientButton";
import { FaChevronRight } from "react-icons/fa";
const ease = [0.17, 0.85, 0.438, 0.99] as const;

/**
 * Replace with the real "order management" photograph when the asset is available.
 * Everything else in this section is live markup, so only this path needs changing.
 */
const SECTION_PHOTO = "/images/brokerage/smart-solutions.png";
const CircleCheck = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full border-2 border-[#f5325b] bg-transparent"><svg xmlns="http://www.w3.org/2000/svg" className="h-[13px] w-[13px] text-[#f5325b]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5} aria-hidden={true}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default function SimplifyOrderManagement() {
  const $ = _c(35);
  const desktopAnimations = useDesktopAnimations();
  let t0;
  if ($[0] !== desktopAnimations) {
    t0 = desktopAnimations ? {
      opacity: 0
    } : false;
    $[0] = desktopAnimations;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== desktopAnimations) {
    t1 = desktopAnimations ? {
      opacity: 1
    } : undefined;
    $[2] = desktopAnimations;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== desktopAnimations) {
    t2 = desktopAnimations ? undefined : {
      opacity: 1
    };
    $[4] = desktopAnimations;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      duration: 0.6
    };
    t4 = {
      once: true
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image src={SECTION_PHOTO} alt="Managing brokerage orders on a tablet" width={600} height={500} className="h-auto w-full object-contain" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t6 = <motion.div initial={t0} whileInView={t1} animate={t2} transition={t3} viewport={t4} className="relative order-1 mx-auto w-full max-w-[600px] lg:ml-0">{t5}</motion.div>;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== desktopAnimations) {
    t7 = desktopAnimations ? {
      opacity: 0,
      y: 40
    } : false;
    $[13] = desktopAnimations;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== desktopAnimations) {
    t8 = desktopAnimations ? {
      opacity: 1,
      y: 0
    } : undefined;
    $[15] = desktopAnimations;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== desktopAnimations) {
    t9 = desktopAnimations ? undefined : {
      opacity: 1,
      y: 0
    };
    $[17] = desktopAnimations;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      duration: 0.6,
      ease
    };
    t11 = {
      once: true
    };
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t12 = <motion.h2 initial={t7} whileInView={t8} animate={t9} transition={t10} viewport={t11} className="font-poppins mb-5 max-w-[620px] text-[30px] font-semibold leading-[1.4] text-[#0b1354] sm:text-[36px] lg:text-[40px] dark:text-white">Simplify Order Management with Smart Solutions</motion.h2>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="font-poppins mb-4 text-base font-semibold text-[#0b1354] lg:text-[17px] dark:text-white">Boost Efficiency and Gain Control with an All-in-One Platform</p>;
    t14 = <p className="mb-8 text-justify text-[16px] leading-[30px] text-[#6b7280] lg:text-[17px] dark:text-gray-400">Our powerful order management app streamlines your brokerage business, helping you manage orders, track progress, and improve overall efficiency. Spend less time on manual tasks and focus on driving growth with an intuitive solution designed to simplify your workflow.</p>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t13 = $[25];
    t14 = $[26];
  }
  let t15;
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ul className="mb-7 space-y-5">{simplifyPoints.map(_temp)}</ul>;
    t16 = <Link href="/contact" className="mb-7 w-fit text-base text-[#1877e6] transition-colors duration-300 hover:text-[#0b5cc4] lg:text-[17px] dark:text-blue-400 dark:hover:text-blue-300">Launch Your Brokerage Management App</Link>;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div><GradientButton href="/contact" variant="peach-pink">Get a Free Consultation <FaChevronRight className="ml-1 text-[12px]" /></GradientButton></div>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t12) {
    t18 = <div className="order-2 flex flex-col justify-center">{t12}{t13}{t14}{t15}{t16}{t17}</div>;
    $[30] = t12;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t18 || $[33] !== t6) {
    t19 = <section className="overflow-hidden bg-white px-[15px] py-16 font-sans md:py-24 dark:bg-gray-900"><div className="mx-auto grid w-full max-w-[1290px] grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-12">{t6}{t18}</div></section>;
    $[32] = t18;
    $[33] = t6;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  return t19;
}
function _temp(point) {
  return <li key={point.title} className="flex items-center gap-3.5 transition-transform duration-300 hover:translate-x-2"><CircleCheck /><span className="font-poppins text-[17px] font-bold text-[#0b1354] lg:text-[18px] dark:text-white">{point.title}</span></li>;
}